var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useMemo, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet/es/Helmet';
import { useSelector, useDispatch } from 'react-redux';
import { destroy, getFormValues } from 'redux-form';
import { apiGetListRfpQuestion, apiCreateRfpQuestion, } from '@/actions/ProfessionalRfpQuestionActions';
import { CustomRedirect } from '@/components/functional/Link/CustomRedirect/CustomRedirect';
import { notify, wrapDispatch, wrapNotifyError } from '@/helpers/action';
import { propShape } from '@/helpers/custom-prop-types';
import { compose } from '@/helpers/recompose';
import { useUnmountRef } from '@/helpers/use/useHook';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import Layout from '../../../Layout';
import Breadcrumb from '../../../Layout/Breadcrumb';
import AuthenticationHoc from '../../../System/AuthenticationHoc';
import RfpPageTitle from '../../../Ui/Rfp/RfpPageTitle';
import RfpHoc from '../Hoc/RfpHoc';
import { rfpQuestionUseCase } from '../UseCase/RfpQuestionUseCase';
import RfpQuestionHoc from './Hoc/RfpQuestionHoc';
import RfpQuestionSkeleton from './Parts/RfpQuestionSkeleton';
import RfpQuestionConfirmContent from './RfpQuestionConfirmContent';
/**
 * 質問入力フォームの確認ページ
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const RfpQuestionConfirm = (props) => {
    const { rfp } = props;
    const { id, name } = rfp;
    const title = '質問内容の確認';
    const pans = useMemo(() => [
        {
            link: '/mypage',
            text: 'マイページ',
        },
        {
            link: '/project/professional',
            text: '募集中のプロジェクト',
        },
        {
            link: `/project/professional/${id}`,
            text: name,
        },
        {
            link: `/project/professional/${id}/question`,
            text: '質問一覧',
        },
        {
            text: title,
        },
    ], [id, name, title]);
    const history = useNavigate();
    const dispatch = useDispatch();
    const prevPath = `/project/professional/${id}/question/add`;
    const currentPath = `/project/professional/${id}/question/confirm`;
    const formName = 'RfpQuestionAddForm';
    const hasForm = useSelector((state) => !!getFormValues(formName)(state));
    const unmountRef = useUnmountRef();
    const useCase = rfpQuestionUseCase.useQuestionConfirmModal(dispatch, formName);
    /**
     * submit時のイベント
     * @type {(function(*=): Promise<void>)|*}
     */
    const onSubmit = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        yield wrapNotifyError(() => __awaiter(void 0, void 0, void 0, function* () {
            // 投稿を保存する
            yield wrapDispatch(dispatch, apiCreateRfpQuestion({ json: values, path: { id } }, {}));
            // 再描画用に最新のデータを引き直す
            yield wrapDispatch(dispatch, apiGetListRfpQuestion({ path: { id } }, { id }));
            if (!unmountRef.current) {
                // 投稿のTOPに戻しつつ画面通知を表示
                history.push(`/project/professional/${id}/question`);
                notify('質問を投稿しました');
            }
        }));
    }), [history, id, dispatch, unmountRef]);
    useEffect(() => () => {
        if (window.location.pathname !== currentPath &&
            window.location.pathname !== prevPath) {
            // ページ離脱時、遷移先が入力フォーム以外であればフォーム内容を削除する
            dispatch(destroy(formName));
        }
    }, [formName, dispatch, prevPath, currentPath]);
    if (!hasForm) {
        // フォーム内容が存在しない場合は質問TOPに戻す
        return _jsx(CustomRedirect, { to: `/project/professional/${id}/question` });
    }
    return (_jsxs(Layout, Object.assign({ mainClassName: 'l-main rfp_question_container rfp_common_container' }, { children: [_jsx(Helmet, { children: _jsx("title", { children: `${title}｜PROFFIT` }) }), _jsx(Breadcrumb, { lists: pans }), _jsx(RfpPageTitle, { text: name }), _jsx("div", Object.assign({ className: 'rfp_container' }, { children: _jsx("div", Object.assign({ className: 'rfp_content' }, { children: _jsx("div", Object.assign({ className: 'rfp_content_main' }, { children: _jsxs("div", Object.assign({ className: 'rfp-card is-rfp' }, { children: [_jsx("div", Object.assign({ className: 'rfp-card-head align-center' }, { children: _jsx("span", { children: title }) })), _jsx("div", Object.assign({ className: 'rfp-card-body' }, { children: _jsx("div", Object.assign({ className: 'rfp-card-form' }, { children: _jsx(RfpQuestionConfirmContent, { form: formName, id: id, onSubmit: onSubmit, useCase: useCase }) })) }))] })) })) })) }))] })));
};
RfpQuestionConfirm.propTypes = {
    rfp: propShape().isRequired,
};
const enhance = compose(AuthenticationHoc({
    isCanUseProffit: true,
    isPreProfessional: false,
}), RfpHoc(rfpQuestionUseCase.questionRedirect, RfpQuestionSkeleton), RfpQuestionHoc({}, RfpQuestionSkeleton));
export default enhance(RfpQuestionConfirm);
